import chalk from 'chalk';
import ora from 'ora';
import type { CacheManager, CacheStats } from './types';

/**
 * 缓存预热和智能清理策略管理器
 * 提供智能的缓存预热、清理和优化策略
 */
export class CacheStrategyManager {
  private cacheManager: CacheManager;
  private spinner = ora();
  private cleanupThresholds = {
    maxSize: 500 * 1024 * 1024, // 500MB
    maxItems: 10000,
    maxAge: 30 * 24 * 60 * 60 * 1000, // 30天
    lowHitRate: 0.3, // 30%
  };

  constructor(cacheManager: CacheManager) {
    this.cacheManager = cacheManager;
  }

  /**
   * 预热缓存
   */
  async warmup(keys: string[]): Promise<void> {
    this.spinner.start(`预热缓存 (${keys.length} 项)...`);

    try {
      const startTime = Date.now();
      let successCount = 0;
      let failCount = 0;

      // 并行预热缓存
      const promises = keys.map(async key => {
        try {
          // 尝试从云端获取缓存
          const value = await this.cacheManager.get(key);
          if (value) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
          console.warn(chalk.yellow(`预热缓存失败: ${key}`), error);
        }
      });

      await Promise.all(promises);

      const duration = Date.now() - startTime;
      this.spinner.succeed(
        `缓存预热完成 (${successCount} 成功, ${failCount} 失败, ${duration}ms)`
      );
    } catch (error) {
      this.spinner.fail('缓存预热失败');
      console.error(chalk.red('预热错误:'), error);
      throw error;
    }
  }

  /**
   * 智能清理缓存
   */
  async smartCleanup(): Promise<{
    cleanedItems: number;
    freedSpace: number;
    strategy: string;
  }> {
    this.spinner.start('执行智能缓存清理...');

    try {
      const stats = await this.cacheManager.getStats();
      const cleanupStrategy = this.selectCleanupStrategy(stats);

      let cleanedItems = 0;
      let freedSpace = 0;

      switch (cleanupStrategy.type) {
        case 'size-based':
          const sizeResult = await this.cleanupBySize(
            cleanupStrategy.threshold
          );
          cleanedItems = sizeResult.cleanedItems;
          freedSpace = sizeResult.freedSpace;
          break;
        case 'age-based':
          const ageResult = await this.cleanupByAge(cleanupStrategy.threshold);
          cleanedItems = ageResult.cleanedItems;
          freedSpace = ageResult.freedSpace;
          break;
        case 'hit-rate-based':
          const hitRateResult = await this.cleanupByHitRate(
            cleanupStrategy.threshold
          );
          cleanedItems = hitRateResult.cleanedItems;
          freedSpace = hitRateResult.freedSpace;
          break;
        case 'frequency-based':
          const freqResult = await this.cleanupByFrequency(
            cleanupStrategy.threshold
          );
          cleanedItems = freqResult.cleanedItems;
          freedSpace = freqResult.freedSpace;
          break;
      }

      this.spinner.succeed(
        `智能清理完成 (${cleanedItems} 项, ${this.formatBytes(freedSpace)})`
      );
      return {
        cleanedItems,
        freedSpace,
        strategy: cleanupStrategy.type,
      };
    } catch (error) {
      this.spinner.fail('智能清理失败');
      console.error(chalk.red('清理错误:'), error);
      throw error;
    }
  }

  /**
   * 清理过期缓存
   */
  async cleanupExpired(): Promise<{
    cleanedItems: number;
    freedSpace: number;
  }> {
    this.spinner.start('清理过期缓存...');

    try {
      let cleanedItems = 0;
      let freedSpace = 0;

      // 这里需要访问具体的缓存实现来检查过期时间
      // 由于接口限制，我们使用通用的清理方法
      await this.cacheManager.cleanup();

      this.spinner.succeed('过期缓存清理完成');
      return { cleanedItems, freedSpace };
    } catch (error) {
      this.spinner.fail('过期缓存清理失败');
      console.error(chalk.red('清理错误:'), error);
      throw error;
    }
  }

  /**
   * 优化缓存策略
   */
  async optimizeStrategy(): Promise<{
    recommendations: string[];
    estimatedImprovement: number;
  }> {
    this.spinner.start('分析缓存策略...');

    try {
      const stats = await this.cacheManager.getStats();
      const recommendations: string[] = [];
      let estimatedImprovement = 0;

      // 分析命中率
      if (stats.hitRate < 0.5) {
        recommendations.push('命中率较低，建议增加缓存预热');
        estimatedImprovement += 0.1;
      }

      // 分析缓存大小
      if (stats.totalSize > this.cleanupThresholds.maxSize) {
        recommendations.push('缓存大小过大，建议启用智能清理');
        estimatedImprovement += 0.05;
      }

      // 分析访问模式
      if (stats.topItems.length > 0) {
        const topItemAccessCount = stats.topItems[0].accessCount;
        const avgAccessCount =
          stats.topItems.reduce(
            (sum: number, item: any) => sum + item.accessCount,
            0
          ) / stats.topItems.length;

        if (topItemAccessCount > avgAccessCount * 3) {
          recommendations.push('访问模式不均匀，建议优化缓存键分布');
          estimatedImprovement += 0.08;
        }
      }

      // 分析平均访问时间
      if (stats.avgAccessTime > 100) {
        recommendations.push('平均访问时间过长，建议优化缓存存储');
        estimatedImprovement += 0.12;
      }

      this.spinner.succeed(`策略分析完成 (${recommendations.length} 条建议)`);
      return {
        recommendations,
        estimatedImprovement: Math.min(estimatedImprovement, 0.3), // 最多30%的改进
      };
    } catch (error) {
      this.spinner.fail('策略分析失败');
      console.error(chalk.red('分析错误:'), error);
      throw error;
    }
  }

  /**
   * 批量预热缓存
   */
  async batchWarmup(
    warmupGroups: Array<{
      keys: string[];
      priority: 'high' | 'medium' | 'low';
      timeout?: number;
    }>
  ): Promise<void> {
    this.spinner.start(`批量预热缓存 (${warmupGroups.length} 组)...`);

    try {
      // 按优先级排序
      const sortedGroups = warmupGroups.sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      });

      // 按优先级顺序预热
      for (const group of sortedGroups) {
        const timeout = group.timeout || 30000; // 默认30秒超时

        try {
          await Promise.race([
            this.warmup(group.keys),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('预热超时')), timeout)
            ),
          ]);
        } catch (error) {
          console.warn(chalk.yellow(`预热组失败 (${group.priority}):`), error);
        }
      }

      this.spinner.succeed('批量预热完成');
    } catch (error) {
      this.spinner.fail('批量预热失败');
      console.error(chalk.red('批量预热错误:'), error);
      throw error;
    }
  }

  /**
   * 选择清理策略
   */
  private selectCleanupStrategy(stats: CacheStats): {
    type: 'size-based' | 'age-based' | 'hit-rate-based' | 'frequency-based';
    threshold: number;
  } {
    // 基于缓存大小
    if (stats.totalSize > this.cleanupThresholds.maxSize) {
      return {
        type: 'size-based',
        threshold: this.cleanupThresholds.maxSize * 0.8, // 清理到80%
      };
    }

    // 基于命中率
    if (stats.hitRate < this.cleanupThresholds.lowHitRate) {
      return {
        type: 'hit-rate-based',
        threshold: this.cleanupThresholds.lowHitRate,
      };
    }

    // 基于访问频率
    if (stats.topItems.length > 0) {
      const avgAccessCount =
        stats.topItems.reduce((sum, item) => sum + item.accessCount, 0) /
        stats.topItems.length;
      return {
        type: 'frequency-based',
        threshold: avgAccessCount * 0.5, // 清理访问次数低于平均值50%的缓存
      };
    }

    // 默认基于年龄
    return {
      type: 'age-based',
      threshold: this.cleanupThresholds.maxAge,
    };
  }

  /**
   * 基于大小的清理
   */
  private async cleanupBySize(_threshold: number): Promise<{
    cleanedItems: number;
    freedSpace: number;
  }> {
    // 这里需要具体的实现逻辑
    // 由于接口限制，返回模拟数据
    return { cleanedItems: 0, freedSpace: 0 };
  }

  /**
   * 基于年龄的清理
   */
  private async cleanupByAge(_threshold: number): Promise<{
    cleanedItems: number;
    freedSpace: number;
  }> {
    // 这里需要具体的实现逻辑
    return { cleanedItems: 0, freedSpace: 0 };
  }

  /**
   * 基于命中率的清理
   */
  private async cleanupByHitRate(_threshold: number): Promise<{
    cleanedItems: number;
    freedSpace: number;
  }> {
    // 这里需要具体的实现逻辑
    return { cleanedItems: 0, freedSpace: 0 };
  }

  /**
   * 基于访问频率的清理
   */
  private async cleanupByFrequency(_threshold: number): Promise<{
    cleanedItems: number;
    freedSpace: number;
  }> {
    // 这里需要具体的实现逻辑
    return { cleanedItems: 0, freedSpace: 0 };
  }

  /**
   * 格式化字节大小
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
